import { Button, MenuItem, Position } from "@blueprintjs/core";
import React, { useLayoutEffect, useRef } from "react";
import { MultiSelect } from "@blueprintjs/select";
import "styled-components/macro";

const ENTER_RETURN_KEY_CODE = 13;

export type CallTopicCodeName =
  | "no_answer"
  | "left_voicemail"
  | "not_in_service"
  | "apology"
  | "routine_checkin"
  // | "promo_offered"
  | "contact_info_incorrect"
  | "source_is_cer_review"
  | "redirected_to_other_contact"
  | "other";

export interface CallTopic {
  codeName: CallTopicCodeName;
  longName: string;
}

export const CallTopicsMS = MultiSelect.ofType<CallTopic>();

const DEFAULT_REASONS: CallTopic[] = [
  { codeName: "left_voicemail", longName: "Left Voicemail Message" },
  { codeName: "no_answer", longName: "Didn't Answer Phone" },
  {
    codeName: "redirected_to_other_contact",
    longName: "Redirected to Other Contact",
  },
  { codeName: "contact_info_incorrect", longName: "Incorrect Contact Info" },
  { codeName: "routine_checkin", longName: "Routine Check-In" },
  { codeName: "other", longName: "Other" },
  { codeName: "source_is_cer_review", longName: "Source: CER Review" },
  { codeName: "not_in_service", longName: "Phone Number No Longer In Service" },
  { codeName: "apology", longName: "Apology" },
  // { codeName: "promo_offered", longName: "Promo Offered" },
];

export function CallTopicsInput({
  queryText,
  setQueryText,
  selectedItems,
  onAdd,
  onClear,
  onRemove,
  activeField,
  setActiveField,
}) {
  const callTopicInputRef = useRef(null);

  const handleFocus = () => {
    callTopicInputRef.current.focus();
    setActiveField("callTopics");
  };
  // const handleBlur = ev => {
  //   ev.preventDefault();
  //   callTopicInputRef.current.blur();
  //   setActiveField(null);
  // };

  const clearButton =
    selectedItems.length > 0 ? (
      <Button icon="cross" minimal={true} onClick={onClear} />
    ) : undefined;
  const isItemSelected = (r) => selectedItems.includes(r);
  const onItemSelect = (callTopic) => {
    if (isItemSelected(callTopic)) {
      onRemove(callTopic.codeName);
    } else {
      onAdd(callTopic);
    }
  };
  const onItemsSelect = (callTopics) => callTopics.map(onItemSelect);
  useLayoutEffect(() => {
    // noinspection JSUnresolvedFunction
    handleFocus();
    // eslint-disable-next-line
  }, []);

  return (
    <div
      css={`
        .bp3-multi-select-popover .bp3-menu {
          //max-width: none !important;
          max-height: 220px !important;
          overflow-y: auto;
        }
      `}
    >
      <CallTopicsMS
        fill
        createNewItemFromQuery={undefined}
        onKeyDown={(ev) => {
          if (ev.keyCode === ENTER_RETURN_KEY_CODE) {
            ev.preventDefault();
          }
        }}
        resetOnSelect={true}
        createNewItemRenderer={null}
        openOnKeyDown={false}
        items={DEFAULT_REASONS}
        itemsEqual="codeName"
        itemDisabled={isItemSelected}
        itemPredicate={callTopicsPredicate}
        itemRenderer={callTopicItemRenderer(isItemSelected)}
        selectedItems={selectedItems}
        onItemSelect={onItemSelect}
        tagRenderer={callTopicTagRenderer}
        onItemsPaste={onItemsSelect}
        placeholder="Choose all that apply..."
        query={queryText}
        onQueryChange={setQueryText}
        // onFocus={handleFocus}
        popoverProps={{
          usePortal: false,
          minimal: true,
          position: Position.BOTTOM_LEFT,
        }}
        tagInputProps={{
          onFocus: handleFocus,
          name: "callTopics",
          autoFocus: true,
          inputRef: (r) => {
            callTopicInputRef.current = r;
          },
          leftIcon: "locate",
          large: true,
          onRemove: onRemove,
          rightElement: clearButton,
          tagProps: { large: false, minimal: true },
        }}
        // TODO: Center for IE11
        // css={`
        //   .bp3-popover-content .bp3-menu {
        //     width: ${width}px;
        //   }
        // `}
      />
    </div>
  );
}

const callTopicsPredicate = (
  query,
  callTopic: CallTopic,
  _index,
  exactMatch
) => {
  const normalizedTitle = callTopic.codeName.toLowerCase();
  const normalizedQuery = query.toLowerCase();

  if (exactMatch) {
    return normalizedTitle === normalizedQuery;
  } else {
    return normalizedTitle.includes(normalizedQuery);
  }
};
const callTopicTagRenderer = (callTopic: CallTopic) => callTopic.longName;
const callTopicItemRenderer = (isItemSelected) => (
  callTopic: CallTopic,
  { modifiers, handleClick }
) => {
  if (!modifiers.matchesPredicate) {
    return null;
  }
  const isSelected = isItemSelected(callTopic);
  return (
    <MenuItem
      active={modifiers.active}
      icon={isSelected ? "tick" : "blank"}
      disabled={false}
      key={callTopic.codeName}
      // label={callTopic.longName}
      onClick={handleClick}
      text={callTopic.longName}
      shouldDismissPopover={false}
    />
  );
};
